import * as R from 'ramda';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import t from 'tcomb';
import {validate} from 'tcomb-validation';
import Logger from 'js/common/logger';
import Bluebird from 'bluebird';

import * as Actions from 'js/creator/actions';
import * as SmartQuestionDucks from 'js/creator/ducks/smart_questions';
import * as UserProfileDucks from 'js/creator/ducks/user_profile';
import {track} from 'js/common/analytics';
import SmartQuestion from 'js/execution/v2/panels/smart_questions_panel/shared_proptypes/smart_question';
import {ChildrenComponents} from 'js/components/prop_types';
import {RegionsByCountryShortCode} from 'js/country_region_helper/index';
import {FullString} from 'js/tcomb_types';
import RequestHelper from 'js/common/request_helper';
import {toTitleCase} from 'js/common/string_helper';
import {MAPS} from 'js/common';
import {goToDashboard} from 'js/creator/history';

import SmartQuestionsContainer from 'js/components/smart_question/index';
import ControlledInput from 'js/components/controlled_input/index';
import AddressInfoForm, {addressInfoKeys} from 'js/creator/components/address_info_form/index';
import CreatorDashboardButton from 'js/creator/components/dashboard_button';
import LoadingSpinner from 'js/components/loading_spinner';

import './index.scss';

const {PublisherToGender} = MAPS;

/**
 * Will only render children if certain criteria are met.
 */
class CampaignQualificationContainer extends Component {
    static propTypes = {
        inviteId: PropTypes.string.isRequired,
        campaign: PropTypes.shape({
            id: PropTypes.number.isRequired
        }),
        submitAnswers: PropTypes.func.isRequired,
        smartQuestionProps: PropTypes.shape({
            questions: PropTypes.arrayOf(SmartQuestion),
            currentResult: PropTypes.bool,
            // autoSubmit == null don't submit, == false submit failure, == true submit success
            autoSubmit: PropTypes.bool,
            handleSubmit: PropTypes.func.isRequired
        }),
        campaignIsShippingProduct: PropTypes.bool.isRequired,
        children: ChildrenComponents,
        user: PropTypes.object,
        showNotification: PropTypes.func.isRequired
    };

    static defaultProps = {
        inviteId: null,
        smartQuestionProps: {}
    };

    static getSchema = ({country}) => {
        const baseSchema = {
            first_name: FullString,
            last_name: FullString,
            address: FullString,
            address_line_2: t.maybe(FullString),
            city: FullString,
            country: FullString,
            state: FullString,
            zip_code: FullString,
            phone_number: FullString
        };

        if (country) {
            return R.merge(baseSchema, {
                state: t.enums(RegionsByCountryShortCode[country])
            });
        }

        return baseSchema;
    };

    constructor(props) {
        super(props);

        this.state = {
            transmittingSmartQuestionAnswers: false,
            ...this.initializeAddressConfirmationState(props),
            ...this.initializeSmartQuestionAnswerState(props),
            hasSubmit: false,
            // Show a spinner while we perform the auto-submit
            transmitting: props.smartQuestionProps.autoSubmit
        };
    }

    async UNSAFE_componentWillMount() {
        const {
            smartQuestionProps: {autoSubmit}
        } = this.props;

        if (autoSubmit !== null) {
            try {
                await this.submitSmartQuestionAnswers();
            } catch (error) {
                Logger.error('Error auto-submitting smart questions', {
                    error,
                    state: R.clone(this.state),
                    props: R.clone(this.props)
                });
            }

            this.setState({transmitting: false});
        }
    }

    /**
     * this.props doesn't exist yet as of the calling of this function
     * @param {Object} props
     * @memberof CampaignQualificationContainer
     */
    initializeAddressConfirmationState = props => ({
        ...R.pickAll(
            [
                'first_name',
                'last_name',
                'address',
                'address_line_2',
                'city',
                'country',
                'state',
                'zip_code',
                'phone_number'
            ],
            props.user.profile || {}
        )
    });

    initializeSmartQuestionAnswerState = props =>
        R.ifElse(
            R.pipe(R.path(['smartQuestionProps', 'smart_questions']), R.isEmpty),

            R.always({}),

            R.pipe(
                R.path(['smartQuestionProps', 'questions']),
                R.pluck('id'),
                R.map(smartQuestionId => {
                    if (smartQuestionId === 'gender') {
                        return {
                            gender: R.pipe(
                                R.prop('publisher_type'),
                                /** Map Publisher Type to Gender */
                                R.prop(R.__, PublisherToGender),
                                /** Transform to {value: gender} */
                                R.objOf('value'),
                                /**
                                 * Smart question checker stupidly checks
                                 * Exact equality and the form on the admin
                                 * execution page stores {label, value}
                                 */
                                ({value}) => {
                                    if (value) {
                                        return {label: toTitleCase(value), value};
                                    }

                                    return {value};
                                }
                            )(R.pathOr({}, ['user', 'profile'], props))
                        };
                    }

                    if (smartQuestionId === 'country') {
                        return {country: R.path(['user', 'profile', 'country'], props)};
                    }

                    if (smartQuestionId === 'state') {
                        return {state: R.path(['user', 'profile', 'state'], props)};
                    }

                    if (smartQuestionId === 'age') {
                        return {age: R.path(['user', 'profile', 'birth_date'], props)};
                    }

                    if (smartQuestionId === 'children') {
                        return {children: R.path(['user', 'profile', 'children'], props)};
                    }

                    if (smartQuestionId === 'gps_radius') {
                        return {
                            gps_radius: R.pipe(
                                R.path(['user', 'profile']),
                                getGpsRadiusStateFromProfile
                            )(props)
                        };
                    }
                }),
                R.mergeAll,
                R.objOf('smartQuestionAnswers')
            )
        )(props);

    hasSmartQuestions = () => {
        const {
            smartQuestionProps: {questions}
        } = this.props;

        return questions && !R.isEmpty(questions);
    };

    hasQuestion = smartQuestionId => {
        const {
            smartQuestionProps: {questions}
        } = this.props;

        return (
            this.hasSmartQuestions() &&
            R.pipe(R.pluck('id'), R.any(R.equals(smartQuestionId)))(questions)
        );
    };

    getQuestionById = id => {
        const {
            smartQuestionProps: {questions}
        } = this.props;

        return R.find(R.propEq('id', id), questions);
    };

    isInvalid = () => {
        const {
            smartQuestionProps: {questions}
        } = this.props;

        const {smartQuestionAnswers} = this.state;

        const validation = validate(
            this.state,
            t.struct(CampaignQualificationContainer.getSchema(this.state))
        );

        const didNotAnswerAllSmartQuestions =
            this.shouldShowSmartQuestionsForm() &&
            R.length(R.keys(smartQuestionAnswers)) !== R.length(questions);

        const didNotCorrectlyFillOutAddressInfo =
            this.shouldShowAddressForm() && R.not(validation.isValid());

        /**
         * If showing a form, validate that its submission criteria is met.
         */
        return didNotAnswerAllSmartQuestions || didNotCorrectlyFillOutAddressInfo;
    };

    shouldBypassQualification = () =>
        this.props.bypassInviteQualification || this.props.isInviteAccepted || this.state.hasSubmit;

    shouldDisableSubmission = () => {
        const {autoSubmit} = this.props.smartQuestionProps;

        if (autoSubmit !== null) {
            return false;
        }

        /** If you auto-failed, don't disable submission. Automatically submit. */
        return this.isInvalid();
    };

    shouldShowAddressForm = () => {
        const {
            smartQuestionProps: {currentResult},
            campaignIsShippingProduct,
            bypassInviteQualification
        } = this.props;

        if (bypassInviteQualification) {
            return false;
        }

        const hasLocationRelatedQuestion =
            this.hasQuestion('country') ||
            this.hasQuestion('state') ||
            this.hasQuestion('gps_radius');

        const needsToAnswerLocationRelatedQuestion =
            hasLocationRelatedQuestion && R.isNil(currentResult);

        return (
            R.not(this.shouldBypassQualification()) &&
            (needsToAnswerLocationRelatedQuestion || campaignIsShippingProduct)
        );
    };

    shouldShowSmartQuestionsForm = () => {
        const {
            smartQuestionProps: {questions, currentResult},
            bypassInviteQualification
        } = this.props;

        if (bypassInviteQualification) {
            return false;
        }

        const hasQuestions = this.hasSmartQuestions();
        const hasNonCountryQuestion =
            hasQuestions &&
            R.pipe(
                R.pluck('id'),
                R.any(id => R.not(R.equals('country', id)))
            )(questions);
        const hasNotSavedAnAnswerYet = R.isNil(currentResult);
        /** Has questions and no current result */

        return (
            R.not(this.shouldBypassQualification()) &&
            hasQuestions &&
            hasNotSavedAnAnswerYet &&
            hasNonCountryQuestion
        );
    };

    shouldHideChildren = () => {
        const {bypassInviteQualification} = this.props;

        if (bypassInviteQualification) {
            return false;
        }

        return (
            R.not(this.shouldBypassQualification()) &&
            (this.shouldShowAddressForm() || this.shouldShowSmartQuestionsForm())
        );
    };

    handleSmartQuestionAnswerGiven = R.curry((id, answerGiven) => {
        this.setState(
            R.evolve({
                smartQuestionAnswers: R.assoc(id, answerGiven)
            })
        );
    });

    handleAddressInfoChange = nextState => {
        this.setState(nextState);

        if (this.hasQuestion('country') && nextState.country) {
            this.handleSmartQuestionAnswerGiven('country', nextState.country);
        }

        if (this.hasQuestion('state') && nextState.state) {
            this.handleSmartQuestionAnswerGiven('state', nextState.state);
        }

        if (this.hasQuestion('gps_radius')) {
            this.handleSmartQuestionAnswerGiven(
                'gps_radius',
                getGpsRadiusStateFromProfile(nextState)
            );
        }
    };

    handleControlledInput = R.curry((stateKey, value) => {
        this.setState({[stateKey]: value});
    });

    submitSmartQuestionAnswers = async () => {
        const {
            submitAnswers,
            smartQuestionProps: {handleSubmit, currentResult},
            campaign: {id: campaignId},
            inviteId,
            user: {email}
        } = this.props;

        const {smartQuestionAnswers} = this.state;

        if (this.shouldDisableSubmission()) {
            return {succeeded: false};
        }

        if (currentResult !== null) {
            return {succeeded: true};
        }

        const {payload} = await submitAnswers(campaignId, {
            submission: R.pipe(
                R.keys,
                R.map(id => ({
                    id,
                    negative_tags: this.getQuestionById(id).negative_tags,
                    positive_tags: this.getQuestionById(id).positive_tags,
                    answer_given: smartQuestionAnswers[id]
                }))
            )(smartQuestionAnswers),
            invite_id: inviteId
        });

        const succeeded = RequestHelper.requestSucceeded(payload);

        if (!succeeded) {
            return {succeeded};
        }

        const wasMatch = R.path(['data', 'is_success'], payload);

        track(email, 'Submit Smart Question', {
            'Smart Question Match?': wasMatch ? 'Yes' : 'No'
        });

        handleSubmit({payload});

        return {succeeded};
    };

    submitProfileUpdate = async () => {
        const {patchProfile, handleShippingInfoEntered} = this.props;

        const patch = R.pipe(
            R.pick([
                'first_name',
                'last_name',
                'address',
                'address_line_2',
                'city',
                'country',
                'state',
                'zip_code',
                'phone_number'
            ]),
            R.assoc('user_id', R.path(['profile', 'user_id'], this.props.user))
        )(this.state);

        const succeeded = await R.pipeP(
            patchProfile,
            R.prop('payload'),
            RequestHelper.requestSucceeded
        )(patch);

        if (!succeeded) {
            return {succeeded};
        }

        handleShippingInfoEntered();

        return {succeeded};
    };

    handleSubmit = async () => {
        this.setState({transmitting: true});

        const updates = {
            smartQuestions: {
                shouldUpdate: this.hasSmartQuestions(),
                doUpdate: this.submitSmartQuestionAnswers
            },
            addressForm: {
                shouldUpdate: this.shouldShowAddressForm(),
                doUpdate: this.submitProfileUpdate
            }
        };

        const allUpdatesSuceeded = await Bluebird.reduce(
            R.keys(updates),
            async (_, updateName) => {
                const {shouldUpdate, doUpdate} = updates[updateName];

                if (shouldUpdate) {
                    const {succeeded} = await doUpdate();

                    return succeeded;
                }

                return true;
            },
            true
        );

        this.setState({hasSubmit: allUpdatesSuceeded, transmitting: false});
    };

    smartQuestionsFiltered = () => {
        const {smartQuestionProps} = this.props;

        if (R.isNil(smartQuestionProps.questions)) {
            return null;
        }

        return R.reject(R.pipe(R.prop('id'), R.contains(R.__, ['country', 'state', 'gps_radius'])))(
            smartQuestionProps.questions
        );
    };

    validateStateKey = key =>
        R.pipe(R.prop(R.__, this.state), value =>
            validate(value, CampaignQualificationContainer.getSchema(this.state)[key]).isValid()
        )(key);

    validationClassName = key => (this.validateStateKey(key) ? 'success' : 'error');

    render() {
        const {smartQuestionProps, children, user} = this.props;

        const {
            first_name,
            last_name,
            address,
            address_line_2,
            city,
            country,
            state,
            zip_code,
            phone_number,
            transmitting
        } = this.state;

        const {smartQuestionAnswers} = this.state;

        const addressInfoProps = {
            address,
            address_line_2,
            city,
            country,
            state,
            zip_code
        };

        const shouldShowAddressForm = this.shouldShowAddressForm();
        const shouldShowSmartQuestionsForm = this.shouldShowSmartQuestionsForm();
        const shouldHideChildren = this.shouldHideChildren();

        if (transmitting) {
            return (
                <div id="qualifications-container">
                    <div id="qualifications-content__transmitting" className="text-center">
                        <LoadingSpinner additionalClasses="fa-3x" />
                    </div>
                </div>
            );
        }

        if (smartQuestionProps.autoSubmit === false || smartQuestionProps.currentResult === false) {
            return (
                <div id="qualifications-container">
                    <div id="qualifications-content__failed" className="text-center">
                        <CreatorDashboardButton onClick={goToDashboard}>
                            Back To Campaigns
                        </CreatorDashboardButton>
                    </div>
                </div>
            );
        }

        return (
            <div id="qualifications-container">
                <div id="qualifications-content">
                    {shouldShowAddressForm && (
                        <AddressInfoForm
                            layout="compact"
                            onChange={this.handleAddressInfoChange}
                            {...addressInfoProps}
                        >
                            <ControlledInput
                                className="col-xs-6"
                                slot={AddressInfoForm.slots.before_form}
                                validationState={this.validationClassName('first_name')}
                                onChange={this.handleControlledInput('first_name')}
                                value={first_name || ''}
                                label="First Name"
                                placeholder=""
                                name="first_name"
                                key="first_name"
                            />
                            <ControlledInput
                                className="col-xs-6"
                                slot={AddressInfoForm.slots.before_form}
                                validationState={this.validationClassName('last_name')}
                                onChange={this.handleControlledInput('last_name')}
                                value={last_name || ''}
                                label="Last Name"
                                placeholder=""
                                name="last_name"
                                key="last_name"
                            />
                            <ControlledInput
                                className="col-xs-12"
                                slot={AddressInfoForm.slots.after_form}
                                validationState={this.validationClassName('phone_number')}
                                value={phone_number || ''}
                                onChange={this.handleControlledInput('phone_number')}
                                label="Phone Number"
                                placeholder=""
                                name="phone_number"
                                key="last_name"
                            />
                        </AddressInfoForm>
                    )}

                    {shouldShowSmartQuestionsForm && (
                        <SmartQuestionsContainer
                            questions={this.smartQuestionsFiltered()}
                            currentResult={smartQuestionProps.currentResult}
                            user={user}
                            answers={smartQuestionAnswers}
                            handleAnswerGiven={this.handleSmartQuestionAnswerGiven}
                        />
                    )}

                    {shouldHideChildren && (
                        <div className="qualifications-button">
                            <CreatorDashboardButton
                                disabled={this.shouldDisableSubmission()}
                                onClick={this.handleSubmit}
                            >
                                {transmitting ? <LoadingSpinner /> : 'Submit'}
                            </CreatorDashboardButton>
                        </div>
                    )}
                </div>

                {R.not(shouldHideChildren) && children}
            </div>
        );
    }
}

/**
 * Don't set gps_radius unless all required parts of the address
 * have been filled in
 */
function getGpsRadiusStateFromProfile(profile) {
    if (R.isNil(profile)) {
        return null;
    }

    const requiredProps = R.without(['address_line_2'], addressInfoKeys);
    const hasAllNecessaryProps = R.pipe(
        R.pickAll(requiredProps),
        R.values,
        R.allPass([R.none(R.isNil), R.none(R.isEmpty)])
    )(profile);

    if (hasAllNecessaryProps) {
        return R.pickAll(addressInfoKeys, profile);
    }
}

export default connect(
    R.pick(['user', 'bypassInviteQualification']),
    R.mergeAll([
        R.pick(['submitAnswers'], SmartQuestionDucks),
        R.pick(['patchProfile'], UserProfileDucks),
        R.pick(['showNotification'], Actions)
    ])
)(CampaignQualificationContainer);



// WEBPACK FOOTER //
// ./components/campaign_qualification_container/index.js